<template>
    <div class="widget-inner">
        <template v-if="isPersonalDashboardWidget">
            <div class="widget-empty-message" v-if="!entries.length">
                <div :style="{color}">
                    {{widget.emptyMessage}}
                </div>
            </div>
            <div class="widget-content" v-else>
                <div class="padding-5">
                    <div class="text-danger font-size-16 padding-bottom-2">
                        <div v-interact="() => toggleOpen('past')" class="no-select pointer"
                            :title="open.includes('past') ? 'Zuklappen' : 'Aufklappen'">
                            <span>
                                <strong>Überfällig ({{pastCount}})</strong>
                            </span>
                            <i class="material-icons" aria-hidden="true" role="img">
                                {{open.includes('past') ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}}
                            </i>
                        </div>
                    </div>
                    <ul :class="{'pd-container-closed': !open.includes('past')}">
                        <li v-for="entry in mappedEntries.past"
                            :class="['flex', {'bottom-border': content.hasMore || entry !== content.entries![content.entries!.length - 1]}]">
                            <div class="text-overflow">
                                <router-link :to="entry.link" v-if="entry.link">{{entry.name}}</router-link>
                                <router-link v-else :to="{name: widget.viewRoute, params: entry}">{{entry.name}}</router-link>
                            </div>
                            <div v-if="entry.hasAppointment" class="flex flex--item-right no-select">
                                <i v-tooltip class="material-icons text-success" aria-hidden="true" title="Diese Wiedervorlage hat einen Termin." role="img">
                                    event
                                </i>
                            </div>
                        </li>
                    </ul>

                    <div class="text-success font-size-16 margin-top-5">
                        <div v-interact="() => toggleOpen('today')" class="no-select pointer"
                            :title="open.includes('today') ? 'Zuklappen' : 'Aufklappen'">
                            <span>
                                <strong>Heute ({{todayCount}})</strong>
                            </span>
                            <i class="material-icons" aria-hidden="true" role="img">
                                {{open.includes('today') ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}}
                            </i>
                        </div>
                    </div>
                    <ul :class="{'pd-container-closed': !open.includes('today')}">
                        <li v-for="entry in mappedEntries.today"
                            :class="['flex', {'bottom-border': content.hasMore || entry !== content.entries![content.entries!.length - 1]}]">
                            <div class="text-overflow">
                                <router-link :to="entry.link" v-if="entry.link">{{entry.name}}</router-link>
                                <router-link v-else :to="{name: widget.viewRoute, params: entry}">{{entry.name}}</router-link>
                            </div>
                            <div v-if="entry.hasAppointment" class="flex flex--item-right no-select">
                                <i v-tooltip class="material-icons text-success" aria-hidden="true" title="Diese Wiedervorlage hat einen Termin." role="img">
                                    event
                                </i>
                            </div>
                        </li>
                    </ul>
                </div>
            </div>

            <router-link v-if="content.hasMore" to="/contact/resubmission/list" class="flex margin-right-10 margin-bottom-7">
                <div class="text-overflow flex--item-right text-underline">
                    <span>Alle ansehen</span>
                    <i class="material-icons small-icon" role="img">open_in_new</i>
                </div>
            </router-link>
        </template>
        <template v-else>
            <div class="widget-empty-message"
                v-if="(!stats.weekago && !stats.last && !stats.today && !stats.weekthis && !stats.weeknext)">
                <div v-if="isPersonalDashboardWidget" :style="{color}">
                    {{widget.emptyMessage}}
                </div>
                <template v-else>
                    <i class="material-icons md-96" aria-hidden="true" role="img">{{widget.emptyIcon}}</i>
                    <br/>
                    {{widget.emptyMessage}}
                </template>
            </div>
            <div class="text-center" style="padding: 15px" v-else>
                <template v-if="stats.weekago">
                    <span style="color: #eb0000; font-weight: bold">Älter als eine Woche: {{stats.weekago}}</span>
                    <br/>
                </template>
                <template v-if="stats.last">
                    <span style="color: #eb0000; font-weight: bold">Überfällig: {{stats.last}}</span>
                    <br/>
                </template>
                <template v-if="stats.today">
                    <span style="color: green; font-weight: bold">Heute: {{stats.today}}</span>
                    <br/>
                </template>
                <template v-if="stats.weekthis">
                    <span style="font-weight: bold">Bis Freitag: {{stats.weekthis}}</span>
                    <br/>
                </template>
                <template v-if="stats.weeknext">
                    <span style="font-weight: bold">Bis nächsten Freitag: {{stats.weeknext}}</span>
                    <br/>
                </template>

                <br/>
                <router-link :to="'/contact/resubmission/list'" v-tooltip="'Zu meinen Wiedervorlagen'" class="button button-danger">Meine Wiedervorlagen</router-link>
            </div>
        </template>
    </div>
</template>

<script lang="ts">
    import {Component} from 'vue-facing-decorator';
    import {WidgetDefault} from './WidgetDefault.vue';

    interface IResubmissionWidgetStats
    {
        weekago: number;
        last: number;
        today: number;
        weekthis: number;
        weeknext: number;
    }

    interface IResubmissionWidgetEntry
    {
        name: string;
        date: string;
        link: string;
        isPast: boolean;
        isToday: boolean;
        hasAppointment: boolean;
    }

    @Component({
        name: 'WidgetResubmission',
    })
    export default class WidgetResubmission extends WidgetDefault
    {
        open: ('past' | 'today')[] = [];

        get todayCount(): number
        {
            return this.content.today as number;
        }

        get pastCount(): number
        {
            return this.content.past as number;
        }

        get entries(): IResubmissionWidgetEntry[]
        {
            return this.content.entries as IResubmissionWidgetEntry[];
        }

        get mappedEntries(): { past: IResubmissionWidgetEntry[], today: IResubmissionWidgetEntry[] }
        {
            return {
                past: this.entries.filter(e => e.isPast),
                today: this.entries.filter(e => e.isToday),
            };
        }

        get stats(): IResubmissionWidgetStats
        {
            return this.content.stats as IResubmissionWidgetStats;
        }

        created(): void
        {
            if (this.isPersonalDashboardWidget)
            {
                if (this.mappedEntries.today.length > 0)
                {
                    this.open.push('today');
                }
                else if (this.mappedEntries.past.length > 0)
                {
                    this.open.push('past');
                }
            }
        }

        toggleOpen(type: 'past' | 'today'): void
        {
            if (this.open.includes(type))
            {
                this.open = this.open.filter(entry => entry !== type);
            }
            else
            {
                this.open.push(type);
            }
        }
    }
</script>